<template>
    <b-row class="mb-3">
        <b-card no-body bg-variant="white" class="w-100">
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">스키마 입력 값 검색하기</h5>

                    <div v-if="loading">
                        <div class="d-flex align-items-center" style="height: 36px">
                            <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                            <p class="d-inline-block mb-0">조회중...</p>
                        </div>
                    </div>
                    <div v-else class="d-flex align-items-center">
                        <b-button form="schema-input-search-form"
                                  type="reset"
                                  :disabled="loading"
                        >
                            {{ $t('초기화') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <div class="p-3">
                <b-form id="schema-input-search-form"
                        autocomplete="new-password"
                        @submit.stop.prevent="searchData"
                        @reset="resetSearch"
                >
                    <b-row>
                        <b-col cols="12" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-project_id"
                                label="프로젝트"
                                label-for="input-project_id"
                                class="mb-0"
                            >
                                <div class="d-flex align-items-center rounded text-secondary w-100">
                                    <!-- 고르거나 지우면(X) 바로 검색한다. v-model 이 먼저 갱신된 뒤 @input 이 불린다 -->
                                    <auto-complete-select id="input-project_id"
                                                          ref="projectCodeAutoComplete"
                                                          v-model="selectedProjectCodeId"
                                                          :options="projectCodeOptions"
                                                          :loading="loading"
                                                          start-expanded
                                                          clearable
                                                          list-max-height="360px"
                                                          option-value-key="id"
                                                          :placeholder="$t('프로젝트를 선택해주세요.')"
                                                          autocomplete="new-password"
                                                          @input="searchData"
                                    >
                                    </auto-complete-select>
                                </div>
                                <div v-if="projectsError" class="text-danger small mt-1">
                                    {{ projectsError }}
                                    <b-button size="sm" variant="link" class="text-danger p-0 align-baseline" @click="getProjectCode">다시 시도</b-button>
                                </div>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-form>

            </div>
        </b-card>


        <b-row class="mt-3">
            <b-col cols="12">
                <b-card
                    :class="[{ 'shadow-sm': searchResults || (!loading && searchResults) }, 'list-container']"
                    :bg-variant="!searchResults ? 'light' : 'white'"
                    no-body
                >
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 class="mb-0">스키마 입력 값 목록</h6>
                            </div>
                            <div class="d-flex align-items-center">
                                <b-button variant="outline-secondary" size="md"
                                          class="mr-2"
                                          @click="openSchemaInputChangeOrderModal">
                                    스키마 입력 값 순서변경
                                </b-button>
                                <b-button variant="outline-primary" size="md"
                                          @click="openSchemaInputModal('add')">
                                    스키마 입력 값 추가
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="table-container">
                        <b-table
                            fixed responsive
                            :items="searchResults"
                            :fields="tableColumns"
                            class="mb-0"
                            show-empty
                            :busy="loading"
                        >
                            <template #empty>
                                <PageEmptyComponent
                                    :text="$t('조회된 스키마 입력 값가 없습니다.')"
                                    :loading="loading"
                                    :loadingText="$t('스키마 입력 값 목록을 조회 중입니다.')"
                                    :error="listError"
                                    @retry="fetchData"
                                />
                            </template>
                            <template #cell(project_id)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(getTargetProjectText(row.value))">
                                    {{ getTargetProjectText(row.value) || `#${row.value}` }}
                                </span>
                            </template>

                            <template #cell(key_name)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value)">{{ row.value }}</span>
                            </template>
                            <template #cell(value_type)="row">
                                <b-badge variant="dark" class="f-80">
                                    {{ getValueTypeText(row.value) }}
                                </b-badge>
                            </template>
                            <template #cell(label_key)="row">
                                <span v-b-tooltip.hover="get_tooltip_title(row.value)" class="text-link">
                                    {{ row.value }}
                                </span>
                            </template>

                            <template #cell(is_required)="row">
                                <b-badge :variant="row.value ? 'success' : null" class="f-80">
                                    {{ getRequiredName(row.value) }}
                                </b-badge>
                            </template>

                            <template #cell(detail)="data">
                                <b-btn size="sm"
                                       variant="outline-primary"
                                       @click="openSchemaInputModal('detail', data.item)">상세
                                </b-btn>
                                <b-btn size="sm"
                                       variant="outline-secondary"
                                       class="ml-1"
                                       @click="openSchemaInputCopy(data.item)">복사
                                </b-btn>
                                <b-btn size="sm"
                                       variant="outline-danger"
                                       class="ml-1"
                                       :disabled="deletingId === data.item.id"
                                       @click="deleteSchemaInput(data.item)">
                                    <b-spinner v-if="deletingId === data.item.id" small/>
                                    삭제
                                </b-btn>
                            </template>

                            <template #cell(updated_at)="row">
                                {{ get_date(row.value) }}

                            </template>
                            <template #cell(created_at)="row">
                                {{ get_date(row.value) }}
                            </template>

                        </b-table>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <schema-input-modal ref="schemaInputModal"
                            :detail-id="detailId"
                            :selectedProjectCodeId="selectedProjectCodeId"
                            :projects="projects"
                            @updateDetailData="resetPage"
                            @addData="resetPage"
                            @deleteData="resetPage"
        />
        <schema-input-order-change-modal :project-text="getTargetProjectText(selectedProjectCodeId)"
                                         :schema-inputs="searchResults"
                                         @schemaInputsOrderChanged="resetPage"
        />

    </b-row>
</template>

<script>
import {listInputKeys} from '@shared_utils/api/projectSchema';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import {
    getProjectCodeOptions,
    getTargetProjectText,
    onTooltipShow
} from '@shared/components/admin/pages/Management/common-data';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import PaginationPageMixin from '@shared/mixin/pagination-page-mixins';
import {columns, valueTypeOptions} from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/data';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import SchemaInputModal
    from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputModal.vue';
import SchemaInputOrderChangeModal
    from '@shared/components/admin/pages/Management/ProjectSchema/SchemaInput/SchemaInputOrderChangeModal.vue';

// URL 의 project_id 는 양의 정수 문자열만 받는다('12' → 12). 빈 값·0·'abc'·'1.5' 는 선택 없음
const toProjectId = (value) => {
    const n = typeof value === 'number' ? value : (/^[1-9]\d*$/.test(String(value ?? '')) ? Number(value) : NaN);
    return Number.isSafeInteger(n) && n > 0 ? n : null;
};

export default {
    name: 'ManageSchemaInput',
    components: {
        SchemaInputOrderChangeModal,
        SchemaInputModal,
        AutoCompleteSelect,
        PageEmptyComponent,
    },
    mixins: [PaginationPageMixin({searchForm: {project_id: null}, pagination: null})],
    props: {
        currentTab: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            selectedProjectCodeId: null,
            projects: null,
            projectsLoading: false,
            projectsError: '',
            listError: '',
            // 늦게 온 이전 검색 응답이 새 결과를 덮지 않게 요청 순번을 센다
            listSeq: 0,

            tableColumns: columns,
            detailId: null,
            deletingId: null,
        };
    },
    computed: {
        loading() {
            return this.listLoading || this.projectsLoading;
        },
        getTargetProjectText() {
            return (project_id) => getTargetProjectText(project_id, this.projects);
        },
        getRequiredName() {
            return (is_required) => {
                return is_required ? this.$t('필수') : this.$t('선택');
            };
        },
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        getValueTypeText() {
            return (value_type) => {
                return valueTypeOptions.find((item) => item.value === value_type)?.text ?? '-';
            };
        },
    },
    watch: {
        // 첫 진입·다른 탭에서 넘어옴·뒤로가기 모두 여기서 처리한다(첫 탭이라 URL 에 tab 이 없으면 이 탭)
        '$route': {
            immediate: true,
            handler() {
                if ((this.$route.query.tab || 'schemaInput') !== 'schemaInput') return;
                this.setSearchDataFormQuery();
                this.selectedProjectCodeId = toProjectId(this.searchFormValue.project_id);
                this.getProjectCode();
                this.fetchData();
            }
        }
    },
    methods: {
        onTooltipShow,
        // 프로젝트 목록은 한 번만 받는다(진행 중이면 다시 요청하지 않음). 실패는 선택기 아래에 다시 시도와 함께 보여 준다
        async getProjectCode() {
            if (this.projects || this.projectsLoading) return;
            this.projectsLoading = true;
            this.projectsError = '';
            try {
                const response = await this.$axios.get('/project_code');
                this.projects = response.data;
            } catch (error) {
                if (error?.response?.status === 401) return;
                this.projectsError = `프로젝트 목록을 불러오지 못했습니다. ${getErrorMessage(error, '')}`.trim();
            } finally {
                this.projectsLoading = false;
            }
        },
        async searchData() {
            this.searchFormValue.project_id = this.selectedProjectCodeId;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        resetPage() {
            this.setSearchDataFormQuery();
            this.fetchData();
        },
        async resetSearch(){
            this.selectedProjectCodeId = null;
            this.searchFormValue.project_id = null;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        async fetchData() {
            const seq = ++this.listSeq;
            try {
                this.listLoading = true;
                this.listError = '';

                const res = await listInputKeys({
                    params: {
                        project_id: this.selectedProjectCodeId,
                    },
                });
                if (seq !== this.listSeq) return;

                const {input_keys = [], total} = res.data || {};

                this.searchResults = input_keys.map((item, index) => {
                    const data = {
                        ...item,
                        value: JSON.stringify(item.value),
                        index: total - index,
                    };
                    return data;
                });
            } catch (error) {
                if (seq !== this.listSeq) return;
                // 실패를 '조회된 스키마 입력 값가 없습니다'로 보이지 않게 이전 결과를 비우고 표 안에 오류를 띄운다
                this.searchResults = [];
                if (error?.response?.status === 401) return;
                this.listError = `스키마 입력 값 목록을 불러오지 못했습니다. ${getErrorMessage(error, '')}`.trim();
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), '스키마 입력 값 검색 도중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
            } finally {
                if (seq === this.listSeq) this.listLoading = false;
            }
        },
        openSchemaInputCopy(item) {
            this.detailId = null;
            this.$bvModal.show('schema-input-detail-modal');
            this.$refs.schemaInputModal.openAsCopy(item.id);
        },
        // 리스트에서 바로 삭제. 기존엔 상세→수정 모드로 들어가야만 삭제 버튼이 보여
        // 발견성이 낮았다. 모달의 삭제와 동일한 confirm + DELETE API 를 재사용한다.
        // 서버가 배정 규칙 참조로 거절하면(400 평문) 그 사유를, 아니면 기본 문구를 보여 준다.
        deleteErrorText(error, fallback) {
            const reason = getErrorMessage(error, '');
            return reason.includes('referenced by campaign rule sets')
                ? '배정 규칙에서 쓰는 질문이라 삭제할 수 없습니다. 규칙을 먼저 고쳐 주세요.' + ` (${reason})`
                : (reason || fallback);
        },
        deleteSchemaInput(item) {
            if (this.deletingId !== null) return;
            const h = this.$createElement;
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('스키마 입력 값 삭제'),
                message: h('div', {style: {whiteSpace: 'pre-line'}},
                    `질문 키: ${item.key_name} / 라벨: ${item.label_key}
프로젝트: ${this.getTargetProjectText(item.project_id)}
이 질문을 쓰는 배정 규칙이 있으면 삭제되지 않습니다. 삭제하면 되돌릴 수 없습니다.`)
            }).then((confirmed) => {
                if (!confirmed) return;
                this.deletingId = item.id;
                this.$axios.delete(`/project-schema/input-keys/${item.id}`)
                    .then(() => {
                        this.$EventBus.$emit('toast-success', this.$t('스키마 입력 값 삭제'), this.$t('스키마 입력 값가 성공적으로 삭제되었습니다.'));
                        this.resetPage();
                    })
                    .catch((error) => {
                        console.error(error);
                        if (error?.response?.status === 401) return;
                        this.$EventBus.$emit('toast-failure', this.$t('스키마 입력 값 삭제'), this.deleteErrorText(error, this.$t('스키마 입력 값 삭제 중 오류가 발생했습니다. 개발 팀에 문의해주세요.')));
                    })
                    .finally(() => {
                        this.deletingId = null;
                    });
            });
        },
        async openSchemaInputModal(mode, item) {
            if (mode === 'add') {
                this.detailId = null;
                this.$refs.schemaInputModal.changeMode(mode);
                this.$bvModal.show('schema-input-detail-modal');
                return;
            }

            this.detailId = item.id;
            this.$refs.schemaInputModal.changeMode(mode);
            this.$bvModal.show('schema-input-detail-modal');
        },
        openSchemaInputChangeOrderModal(){
            // 프로젝트 없이 열면 모든 프로젝트의 질문이 섞인 순서를 저장하게 된다
            if(!this.selectedProjectCodeId){
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: this.$t('프로젝트 선택 필요'),
                    message: this.$t('스키마 입력 값 순서 변경을 위해서는 먼저 프로젝트를 선택해야 합니다.')
                });
                return;
            }
            this.$bvModal.show('schema-input-order-change-modal');
        },
    }
};
</script>

<style lang="scss">
</style>
